import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, dataApi, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Paperclip, Download, Trash2, ImageIcon, FileText, Video, Calendar, Clock, Plus, Pencil, X, Check, Tag as TagIcon, Link2, History, Mountain, Puzzle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LinkedDocsTab from "@/components/LinkedDocsTab";
import { toast } from "sonner";

/* ------------- Phase 1 side cards: Labels / Dependencies / Activity ------------- */

/* ------------- Phase 3 side card: Planning (Epic / Feature) ------------- */

function PlanningCard({ task, projectId, canEdit, onChange }) {
  const [epics, setEpics] = useState([]);
  const [features, setFeatures] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    Promise.all([
      api.get(`/projects/${projectId}/epics`).then((r) => r.data).catch(() => []),
      api.get(`/projects/${projectId}/features`).then((r) => r.data).catch(() => []),
    ]).then(([e, f]) => { setEpics(e); setFeatures(f); });
  }, [projectId]);

  const linkedEpic = epics.find((e) => e.id === task.epic_id);
  const linkedFeature = features.find((f) => f.id === task.feature_id);

  const setParent = async (patch) => {
    setSaving(true);
    try { await api.patch(`/tasks/${task.id}`, patch); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <SectionCard title="Planning" subtitle="Parent Epic & Feature" testid="task-planning-card">
      <div className="space-y-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            <Mountain className="w-3 h-3" />Epic
          </div>
          {canEdit ? (
            <Select
              value={task.epic_id || "none"}
              onValueChange={(v) => setParent({ epic_id: v === "none" ? null : v })}
              disabled={saving}
            >
              <SelectTrigger data-testid="task-epic-sel"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— None —</SelectItem>
                {epics.map((e) => <SelectItem key={e.id} value={e.id}>{e.identifier} · {e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : linkedEpic ? (
            <div><a href={`/epics/${linkedEpic.id}`} className="hover:text-primary">{linkedEpic.identifier} · {linkedEpic.name}</a></div>
          ) : <div className="text-muted-foreground">—</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
            <Puzzle className="w-3 h-3" />Feature
          </div>
          {canEdit ? (
            <Select
              value={task.feature_id || "none"}
              onValueChange={(v) => setParent({ feature_id: v === "none" ? null : v })}
              disabled={saving}
            >
              <SelectTrigger data-testid="task-feature-sel"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— None —</SelectItem>
                {features.map((f) => <SelectItem key={f.id} value={f.id}>{f.identifier} · {f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : linkedFeature ? (
            <div><a href={`/features/${linkedFeature.id}`} className="hover:text-primary">{linkedFeature.identifier} · {linkedFeature.name}</a></div>
          ) : <div className="text-muted-foreground">—</div>}
        </div>
      </div>
    </SectionCard>
  );
}


const REL_KINDS = [
  { v: "blocks",      l: "Blocks" },
  { v: "blocked_by",  l: "Blocked by" },
  { v: "depends_on",  l: "Depends on" },
  { v: "required_by", l: "Required by" },
  { v: "related_to",  l: "Related to" },
  { v: "duplicate",   l: "Duplicate of" },
];

function TaskLabelsCard({ task, projectId, canEdit, onChange }) {
  const [labels, setLabels] = useState([]);
  const [adding, setAdding] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/labels`).then((r) => setLabels(r.data)).catch(() => setLabels([]));
  }, [projectId]);

  const assigned = (task.label_ids || []).map((lid) => labels.find((l) => l.id === lid)).filter(Boolean);
  const available = labels.filter((l) => !(task.label_ids || []).includes(l.id));

  const add = async (lid) => {
    setAdding(true);
    try { await api.post(`/tasks/${task.id}/labels/${lid}`); onChange?.(); setPickOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };
  const remove = async (lid) => {
    try { await api.delete(`/tasks/${task.id}/labels/${lid}`); onChange?.(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <SectionCard title="Labels" subtitle={`${assigned.length} applied`} actions={canEdit && available.length > 0 && (
      <Button size="sm" variant="outline" data-testid="label-add-btn" onClick={() => setPickOpen((v) => !v)}>
        <Plus className="w-3.5 h-3.5 mr-1" />Add label
      </Button>
    )}>
      {pickOpen && available.length > 0 && (
        <div className="mb-3 glass rounded-lg p-2 flex flex-wrap gap-1" data-testid="label-picker">
          {available.map((l) => (
            <button key={l.id} onClick={() => add(l.id)} disabled={adding}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border/60 hover:border-primary/60"
              style={{ color: l.color }}
              data-testid={`label-pick-${l.id}`}
            >
              <TagIcon className="w-3 h-3" />{l.name}
            </button>
          ))}
        </div>
      )}
      {assigned.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">No labels applied.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {assigned.map((l) => (
            <span key={l.id} className="group inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md" style={{ backgroundColor: l.color + "22", color: l.color }}>
              <TagIcon className="w-3 h-3" />
              {l.name}
              {canEdit && (
                <button onClick={() => remove(l.id)} className="opacity-0 group-hover:opacity-100 transition" data-testid={`label-remove-${l.id}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function DependenciesCard({ task, canEdit, onChange }) {
  const [rels, setRels] = useState([]);
  const [kind, setKind] = useState("blocks");
  const [targetTitle, setTargetTitle] = useState("");
  const [options, setOptions] = useState([]);
  const [picking, setPicking] = useState(false);

  const nav = useNavigate();

  useEffect(() => {
    const list = task.relationships || [];
    // Attach title lookup
    const ids = list.map((r) => r.target_task_id);
    if (ids.length === 0) { setRels([]); return; }
    Promise.all(ids.map((id) => api.get(`/tasks/${id}`).then((r) => r.data).catch(() => null)))
      .then((docs) => {
        const titleMap = Object.fromEntries(docs.filter(Boolean).map((d) => [d.id, d.title]));
        setRels(list.map((r) => ({ ...r, target_title: titleMap[r.target_task_id] || "(unknown)" })));
      });
  }, [task.relationships]);

  useEffect(() => {
    if (!task.project_id) return;
    api.get(`/tasks?project_id=${task.project_id}`).then((r) => setOptions(r.data.filter((t) => t.id !== task.id)));
  }, [task.project_id, task.id]);

  const matchedOptions = targetTitle
    ? options.filter((o) => o.title.toLowerCase().includes(targetTitle.toLowerCase())).slice(0, 6)
    : [];

  const addRel = async (targetId) => {
    try {
      await api.post(`/tasks/${task.id}/relationships`, { target_task_id: targetId, kind });
      toast.success("Dependency added");
      setTargetTitle("");
      setPicking(false);
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeRel = async (rid) => {
    try { await api.delete(`/tasks/${task.id}/relationships/${rid}`); onChange?.(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <SectionCard
      title="Dependencies"
      subtitle={`${rels.length} relationship${rels.length === 1 ? "" : "s"}`}
      actions={canEdit && (
        <Button size="sm" variant="outline" data-testid="dep-add-btn" onClick={() => setPicking((v) => !v)}>
          <Link2 className="w-3.5 h-3.5 mr-1" />Link task
        </Button>
      )}
    >
      {picking && (
        <div className="mb-3 glass rounded-lg p-2 space-y-2" data-testid="dep-picker">
          <div className="flex gap-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs rounded-md bg-background/60 border border-border/60 px-2 py-1" data-testid="dep-kind">
              {REL_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
            </select>
            <Input className="h-8 text-xs" placeholder="Search task title…" value={targetTitle} onChange={(e) => setTargetTitle(e.target.value)} data-testid="dep-search" />
          </div>
          {matchedOptions.length > 0 && (
            <div className="space-y-1">
              {matchedOptions.map((o) => (
                <button key={o.id} onClick={() => addRel(o.id)} className="w-full text-left text-xs px-2 py-1 rounded hover:bg-primary/10" data-testid={`dep-opt-${o.id}`}>
                  {o.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {rels.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">No dependencies linked.</div>
      ) : (
        <div className="space-y-1.5">
          {rels.map((r) => {
            const label = REL_KINDS.find((k) => k.v === r.kind)?.l || r.kind;
            return (
              <div key={r.id} className="group flex items-center gap-2 text-sm glass rounded-md px-2.5 py-2" data-testid={`dep-row-${r.id}`}>
                <Badge variant="outline" className="text-[10px] shrink-0">{label}</Badge>
                <button onClick={() => nav(`/tasks/${r.target_task_id}`)} className="flex-1 min-w-0 text-left truncate hover:text-primary">
                  {r.target_title}
                </button>
                {canEdit && (
                  <button onClick={() => removeRel(r.id)} className="opacity-0 group-hover:opacity-100 transition text-red-500" data-testid={`dep-del-${r.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

function ActivityCard({ taskId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/tasks/${taskId}/activity`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [taskId]);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "";
  return (
    <SectionCard title="Activity" subtitle={`${rows.length} event${rows.length === 1 ? "" : "s"}`}>
      {loading ? <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>
        : rows.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">No activity yet.</div>
        : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rows.map((r) => (
              <div key={r.id} className="flex items-start gap-2 text-xs" data-testid={`activity-${r.id}`}>
                <div className="h-6 w-6 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                  <History className="w-3 h-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    <span className="font-medium">{r.actor_name}</span>{" "}
                    <span className="text-muted-foreground">
                      {r.kind.replace(/_/g, " ")}
                      {r.old_value != null && r.new_value != null && (
                        <> · <span className="text-red-400">{String(r.old_value)}</span> → <span className="text-emerald-400">{String(r.new_value)}</span></>
                      )}
                      {r.old_value == null && r.new_value != null && (<> · <span className="text-emerald-400">{String(r.new_value?.target_title || r.new_value?.kind || r.new_value)}</span></>)}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{fmt(r.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </SectionCard>
  );
}

/* ------------- End Phase 1 cards ------------- */


function AttachmentTile({ att, taskId, onDelete, canDelete }) {
  const url = authedUrl(att.url);
  const Icon = att.kind === "image" ? ImageIcon : att.kind === "video" ? Video : FileText;
  return (
    <div data-testid={`attach-${att.id}`} className="group rounded-xl hairline overflow-hidden bg-accent/20 flex flex-col">
      <div className="aspect-video bg-black/20 flex items-center justify-center overflow-hidden">
        {att.kind === "image" ? (
          <img src={url} alt={att.filename} className="w-full h-full object-cover" />
        ) : att.kind === "video" ? (
          <video src={url} className="w-full h-full object-cover" controls />
        ) : (
          <Icon className="w-10 h-10 text-muted-foreground" />
        )}
      </div>
      <div className="p-2 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium truncate">{att.filename}</div>
          <div className="text-[10px] text-muted-foreground">{Math.round(att.size / 1024)} KB · {att.mime}</div>
        </div>
        <a href={url} target="_blank" rel="noreferrer" download={att.filename} data-testid={`dl-${att.id}`} className="p-1.5 rounded hover:bg-accent">
          <Download className="w-3.5 h-3.5" />
        </a>
        {canDelete && (
          <button onClick={() => onDelete(att.id)} data-testid={`del-attach-${att.id}`} className="p-1.5 rounded hover:bg-red-500/10 text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TodoList({ task, canEdit, canToggle, onChange }) {
  const [newTitle, setNewTitle] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);
  const todos = task.todos || [];
  const done = todos.filter((t) => t.is_completed).length;
  const total = todos.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = async (todo) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}/todos/${todo.id}`, { is_completed: !todo.is_completed });
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const add = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/todos`, { title });
      onChange(data);
      setNewTitle("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const rename = async (todo) => {
    const title = editValue.trim();
    if (!title) { setEditing(null); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}/todos/${todo.id}`, { title });
      onChange(data);
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (todo) => {
    if (total <= 1) { toast.error("A task must have at least one todo"); return; }
    if (!window.confirm("Delete this todo?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/tasks/${task.id}/todos/${todo.id}`);
      onChange(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <SectionCard
      title={`Checklist · ${done}/${total}`}
      subtitle="Task completion is driven by these todos"
    >
      <div className="space-y-3" data-testid="todo-section">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden" data-testid="progress-bar">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-medium tabular-nums" data-testid="progress-percent">{pct}%</span>
          </div>
          <div className="text-xs text-muted-foreground">{done} of {total} completed</div>
        </div>

        <div className="space-y-1.5">
          {todos.map((td) => (
            <div key={td.id} data-testid={`todo-${td.id}`} className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50">
              <input
                type="checkbox"
                data-testid={`todo-check-${td.id}`}
                checked={!!td.is_completed}
                disabled={!canToggle || busy}
                onChange={() => toggle(td)}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer disabled:cursor-not-allowed"
              />
              {editing === td.id ? (
                <>
                  <Input
                    autoFocus
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") rename(td); if (e.key === "Escape") setEditing(null); }}
                    className="h-7 text-sm"
                    data-testid={`todo-edit-input-${td.id}`}
                  />
                  <Button size="icon" variant="ghost" onClick={() => rename(td)} className="h-7 w-7"><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditing(null)} className="h-7 w-7"><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <span
                    className={`flex-1 text-sm ${td.is_completed ? "line-through text-muted-foreground" : ""}`}
                    data-testid={`todo-title-${td.id}`}
                  >
                    {td.title}
                  </span>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => { setEditing(td.id); setEditValue(td.title); }}
                        data-testid={`todo-edit-${td.id}`}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-accent"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(td)}
                        data-testid={`todo-del-${td.id}`}
                        disabled={total <= 1}
                        title={total <= 1 ? "A task must have at least one todo" : "Delete"}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/10 text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 pt-2 border-t border-border/40">
            <Input
              placeholder="Add a todo item…"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              className="h-8 text-sm"
              data-testid="todo-new-input"
            />
            <Button size="sm" onClick={add} disabled={!newTitle.trim() || busy} data-testid="todo-add-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/tasks/${id}`).then((r) => setTask(r.data));

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, [id]);

  useEffect(() => {
    if (task?.project_id) {
      const p = projects.find((x) => x.id === task.project_id);
      setProject(p);
    }
  }, [task, projects]);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await dataApi.uploadTaskAttachment(id, file);
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm("Remove this attachment?")) return;
    try {
      await dataApi.deleteTaskAttachment(id, attId);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!task) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const isAdmin = user?.role === "admin";
  const isReporter = task.reporter_id === user?.id;
  const assignee = employees.find((e) => e.id === task.assignee_id);
  const reporter = employees.find((e) => e.id === task.reporter_id);

  return (
    <div className="space-y-6 max-w-5xl">
      <Button data-testid="back-btn" variant="ghost" size="sm" onClick={() => nav("/tasks")}>
        <ArrowLeft className="w-4 h-4 mr-1" />Back to tasks
      </Button>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{project?.name || "—"} · {project?.code || ""}</div>
            <h1 data-testid="task-title-h1" className="font-display font-semibold text-3xl tracking-tighter mt-1">{task.title}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Badge variant="secondary" className="capitalize" data-testid="task-status-badge">{task.status.replace("_", " ")}</Badge>
              <Badge variant="outline" className="capitalize">{task.priority}</Badge>
              {task.due_date && <Badge variant="outline" className="gap-1"><Calendar className="w-3 h-3" />{task.due_date}</Badge>}
              {task.estimated_hours > 0 && <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{task.estimated_hours}h est.</Badge>}
              <Badge
                variant="outline"
                className={`gap-1 ${(task.reopen_count || 0) >= 1 ? "border-orange-400/60 text-orange-600 dark:text-orange-300 bg-orange-100/50 dark:bg-orange-950/40" : ""}`}
                data-testid="reopen-count-badge"
                title="Rework / reopen count"
              >
                <span aria-hidden>🔁</span>
                <span className="tabular-nums">{task.reopen_count || 0}</span>
              </Badge>
            </div>
          </div>
          <div className="flex flex-col gap-3 text-sm min-w-[220px]">
            {assignee && (
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase text-muted-foreground w-20">Assignee</div>
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(assignee.avatar_url)} /><AvatarFallback className="text-[9px]">{assignee.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <span className="text-sm">{assignee.full_name}</span>
              </div>
            )}
            {reporter && (
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase text-muted-foreground w-20">Reporter</div>
                <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(reporter.avatar_url)} /><AvatarFallback className="text-[9px]">{reporter.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                <span className="text-sm">{reporter.full_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="Details">
            <div className="space-y-4 text-sm">
              {task.description ? (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Description</div>
                  <div className="whitespace-pre-wrap leading-relaxed" data-testid="td-desc-view">{task.description}</div>
                </div>
              ) : (
                <div className="text-muted-foreground italic" data-testid="td-desc-view">No description provided.</div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-border/40">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Priority</div>
                  <div className="capitalize mt-0.5" data-testid="td-priority-view">{task.priority || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Est. hours</div>
                  <div className="mt-0.5 tabular-nums">{task.estimated_hours || 0}h</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Due date</div>
                  <div className="mt-0.5 tabular-nums" data-testid="td-due-view">{task.due_date || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</div>
                  <div className="mt-0.5 tabular-nums">{(task.created_at || "").slice(0, 10)}</div>
                </div>
              </div>
            </div>
          </SectionCard>

          <TodoList
            task={task}
            canEdit={isAdmin || isReporter}
            canToggle={isAdmin || isReporter || task.assignee_id === user?.id}
            onChange={(updatedTask) => setTask(updatedTask)}
          />
        </div>

        <div className="space-y-6">
          <PlanningCard
            task={task}
            projectId={task.project_id}
            canEdit={isAdmin || isReporter}
            onChange={() => dataApi.task(id).then((r) => setTask(r.data))}
          />
          <SectionCard title="Documentation" subtitle="Attach runbooks, specs or notes" testid="task-docs-card">
            <LinkedDocsTab entityType="task" entityId={task.id} title="Linked docs" />
          </SectionCard>
          <TaskLabelsCard
            task={task}
            projectId={task.project_id}
            canEdit={isAdmin || isReporter}
            onChange={() => dataApi.task(id).then((r) => setTask(r.data))}
          />
          <DependenciesCard
            task={task}
            canEdit={isAdmin || isReporter}
            onChange={() => dataApi.task(id).then((r) => setTask(r.data))}
          />
          <SectionCard
            title={`Attachments (${(task.attachments || []).length})`}
            subtitle="Images, videos, documents — up to 25 MB each"
            actions={
              <>
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,text/plain" />
                <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-attach-btn">
                  <Paperclip className="w-4 h-4 mr-1" />{uploading ? "Uploading…" : "Upload"}
                </Button>
              </>
            }
          >
            {(task.attachments || []).length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">No attachments yet. Add screenshots, docs, or design files.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(task.attachments || []).map((a) => (
                  <AttachmentTile
                    key={a.id}
                    att={a}
                    taskId={id}
                    onDelete={removeAttachment}
                    canDelete={isAdmin || isReporter || a.uploaded_by === user?.id}
                  />
                ))}
              </div>
            )}
          </SectionCard>
          <ActivityCard taskId={id} />
        </div>
      </div>
    </div>
  );
}
